import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@agion/ui'
import { Check, Loader2, Circle } from 'lucide-react'
import type { OperationLogLine, OperationStatus } from '@/core/infra_types'

interface ProvisionProgressProps {
  logs: OperationLogLine[]
  operationStatus: OperationStatus
}

type StepStatus = 'pending' | 'active' | 'completed' | 'failed'

interface Step {
  label: string
  status: StepStatus
}

/**
 * Parses provision step markers from SSE log stream.
 * ProvisionWorker emits: "=== Step N: ... ===" markers.
 */
function deriveSteps(
  logs: OperationLogLine[],
  operationStatus: OperationStatus,
  t: (key: string, fallback: string) => string,
): Step[] {
  const step1Started = logs.some(l => l.message.includes('Step 1:'))
  const step1Done = logs.some(l => l.message.includes('Infrastructure provisioned successfully'))
  const step2Started = logs.some(l => l.message.includes('Step 2:'))
  const step2Done = logs.some(l => l.message.includes('Agion Edge installed successfully'))
  const allDone = logs.some(l => l.message.includes('Provisioning completed successfully'))
  const isFailed = operationStatus === 'failed'

  const steps: Step[] = [
    {
      label: t('pages:onboarding.provision.step1', 'Provisioning infrastructure'),
      status: step1Done
        ? 'completed'
        : step1Started
          ? (isFailed && !step2Started ? 'failed' : 'active')
          : 'pending',
    },
    {
      label: t('pages:onboarding.provision.step2', 'Installing application'),
      status: step2Done
        ? 'completed'
        : step2Started
          ? (isFailed ? 'failed' : 'active')
          : 'pending',
    },
    {
      label: t('pages:onboarding.provision.step3', 'Ready'),
      status: allDone ? 'completed' : 'pending',
    },
  ]

  return steps
}

const stepIcons: Record<StepStatus, React.ReactNode> = {
  completed: <Check size={16} className="text-emerald-600 dark:text-emerald-400" />,
  active: <Loader2 size={16} className="animate-spin text-blue-600 dark:text-blue-400" />,
  failed: <Circle size={16} className="text-red-500" />,
  pending: <Circle size={16} className="text-slate-300 dark:text-slate-600" />,
}

const stepColors: Record<StepStatus, string> = {
  completed: 'text-emerald-700 dark:text-emerald-400',
  active: 'text-blue-700 dark:text-blue-300 font-medium',
  failed: 'text-red-600 dark:text-red-400',
  pending: 'text-slate-400 dark:text-slate-500',
}

const connectorColors: Record<StepStatus, string> = {
  completed: 'bg-emerald-400 dark:bg-emerald-600',
  active: 'bg-blue-300 dark:bg-blue-700',
  failed: 'bg-red-300 dark:bg-red-700',
  pending: 'bg-slate-200 dark:bg-slate-700',
}

export function ProvisionProgress({ logs, operationStatus }: ProvisionProgressProps) {
  const { t } = useTranslation(['pages'])
  const steps = useMemo(
    () => deriveSteps(logs, operationStatus, t),
    [logs, operationStatus, t],
  )

  return (
    <Card className="mb-4">
      <div className="p-4">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => (
            <div key={step.label} className="flex items-center flex-1">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                  {stepIcons[step.status]}
                </div>
                <span className={`text-sm whitespace-nowrap ${stepColors[step.status]}`}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-3 rounded ${connectorColors[step.status]}`} />
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}
